import axios from 'axios';
import { safeConsoleLog } from './devToolsDetection';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
  headers: {
    'Content-Type': 'application/json'
  },
  // Add timeout to prevent hanging requests
  timeout: 90000 // 90 seconds for AI chat requests (OpenAI can take time)
});

// Track retry attempts for each request
const retryMap = new Map();
const ONBOARDING_PROGRESS_EVENT = 'supplier-onboarding-progress-changed';
const ONBOARDING_PROGRESS_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function shouldNotifySupplierOnboardingProgress(config = {}) {
  const method = config.method?.toLowerCase();
  const url = config.url || '';

  return (
    typeof window !== 'undefined' &&
    ONBOARDING_PROGRESS_METHODS.has(method) &&
    localStorage.getItem('userType') === 'supplier' &&
    url.startsWith('/api/') &&
    !url.includes('/onboarding')
  );
}

// Add a request interceptor to set the auth token
api.interceptors.request.use(
  (config) => {
    // Create or update request identifier 
    const requestId = `${config.method}:${config.url}`;
    
    // Debug logging for upload requests
    if (config.url && config.url.includes('upload')) {}
    
    // Track retry attempts
    if (!retryMap.has(requestId)) {
      retryMap.set(requestId, 0);
    }
    
    const token = localStorage.getItem('token');
    const weddingId = localStorage.getItem('weddingId');
    const userType = localStorage.getItem('userType');
    const businessId = localStorage.getItem('currentBusinessId');

    if (token) {
      config.headers['x-auth-token'] = token;
    }

    // Only send wedding ID for couples, not suppliers or admin
    if (weddingId && userType !== 'supplier' && userType !== 'admin') {
      config.headers['x-wedding-id'] = weddingId;
    }

    // Add userType to headers if available
    if (userType) {
      config.headers['x-user-type'] = userType;
    }
    
    // Add cache-busting headers for chat endpoints
    if (config.url.includes('/chat/conversations')) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }

    // Hard business context: for supplier requests, always send active business ID.
    // Route-level backend checks still enforce actual membership access.
    if (businessId && userType === 'supplier' && config.url?.startsWith('/api/')) {
      config.headers['x-business-id'] = businessId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Clear retry count on successful response
    const requestId = `${response.config.method}:${response.config.url}`;
    retryMap.delete(requestId);
    if (shouldNotifySupplierOnboardingProgress(response.config)) {
      window.dispatchEvent(new Event(ONBOARDING_PROGRESS_EVENT));
    }
    return response;
  },
  async (error) => {
    // Get the URL path from the error config
    const urlPath = error.config?.url || '';
    const userType = localStorage.getItem('userType');
    const requestId = `${error.config?.method}:${urlPath}`;
    const retryCount = retryMap.get(requestId) || 0;
    
    // Handle network errors and timeouts with retry logic (max 2 retries)
    if ((error.code === 'ECONNABORTED' || !error.response) && retryCount < 2) {
      // Increment retry count
      retryMap.set(requestId, retryCount + 1);

      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Retry the request
      return api(error.config);
    }
    
    // Check if this is a Spotify-related endpoint
    const _isSpotifyEndpoint = urlPath.includes('/spotify/');
    
    // Only handle unauthorized access for non-Spotify endpoints
    // or for specific Spotify endpoints that should trigger logout
    if (error.response?.status === 401) {
      // These endpoints should NOT trigger logout (login failures, public endpoints, etc.)
      const noLogoutEndpoints = [
        '/spotify/search',
        '/spotify/connection',
        '/spotify/playlists',
        '/auth/login',
        '/auth/supplier/login',
        '/api/public/', // Public API endpoints should not trigger logout
        '/public/' // Alternative public endpoint pattern
      ];
      
      // Check if we're on a public route (like /profile/:businessId)
      const isPublicRoute = window.location.pathname.startsWith('/profile/');
      
      // Check if this is an endpoint that should not trigger logout
      const isNoLogoutEndpoint = noLogoutEndpoints.some(endpoint => 
        urlPath.includes(endpoint)
      );
      
      // Only logout if it's not in the no-logout list AND not on a public route
      // (i.e., actual auth failures on protected routes)
      if (!isNoLogoutEndpoint && !isPublicRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('supplierRegistrationData');
        localStorage.removeItem('supplierRegistrationStep');
        localStorage.removeItem('supplierRegistrationBusinessId');
        localStorage.removeItem('currentBusinessId');

        // Redirect to appropriate login page based on user type
        if (userType === 'supplier') {
          window.location.href = '/supplier/login';
        } else {
          window.location.href = '/login';
        }
      }
    }
    
    // Check if this is an expected error (early termination fee required)
    // These are part of the normal flow and shouldn't be logged as errors
    const isExpectedError = error.response?.status === 400 && 
      (error.response?.data?.requiresEarlyTerminationFee === true ||
       error.response?.data?.message?.includes('Early termination fee'));
    
    // Only log unexpected errors
    if (!isExpectedError) {
    // Use our safe error logging utility to prevent circular references
    const safeError = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message
    };
    
    safeConsoleLog('API Response Error:', safeError);
    }
    
    return Promise.reject(error);
  }
);

export default api; 